import React, { useRef, useState } from "react";
import { Flex, theme } from "antd";
import { useTranslate } from "@refinedev/core";
import {
  ShareAccessFields,
  Skeleton,
  isValidShareDomain,
  isValidShareEmail,
  isValidSharePassword,
  parseCsvEmails,
  type ShareAccess,
  type ShareRestriction,
} from "@libertify/ui";
import {
  SectionHeading,
  SectionSubtitle,
} from "./GeneralSettingsConfigCard.styles";

const KEY = "createExperience.generalSettings.access";

export interface AccessSharingSectionProps {
  /** The experience's public URL — shown read-only under the Public card,
   *  same row the Share modal shows (`shareUrl` there). */
  shareUrl: string;
  /** The saved access card (from `publication_mode`). `undefined` while the
   *  instance is still loading — the one-time hydration below waits for a
   *  real value instead of defaulting to "public". */
  initialAccess?: ShareAccess;
  /** Fired when Public/Email is saved — the caller owns persistence
   *  (PATCH `publication_mode` + `markAsOutOfDate`). → Email waits for the
   *  first recipient and passes it as `emails`. Password goes through
   *  `onPasswordChange` instead. */
  onAccessChange: (
    access: Exclude<ShareAccess, "password">,
    emails?: string[]
  ) => void;
  /** Fired on blur once the password is valid and confirmed — the caller
   *  PATCHes `PasswordProtected` with it. Never fired with an empty value. */
  onPasswordChange?: (password: string) => void;
  /**
   * Fired with the full authorized-email list whenever it changes while
   * on Email (add, remove, CSV import, or a domain-field blur) — always
   * the whole list, since the backend PATCH replaces it rather than merging.
   * The caller owns persistence; this component never patches on its own.
   */
  onEmailsChange: (emails: string[]) => void;
}

/**
 * "Access & sharing" (MED-6958) — same Public/Password/Email + People/Domain UI
 * as the experience-v2 Share modal, reusing its actual body,
 * `ShareAccessFields` (`libs/ui/.../experience-page/ShareModal`), rather
 * than a re-implementation — including its `experienceV2.share.*` copy,
 * already registered in this app's locale files for that modal. Only the
 * heading/subtitle above it and the commit wiring below are local: this
 * patches each action immediately (on click/blur) instead of batching
 * behind that modal's Save button, since there's no modal chrome here.
 *
 * The backend has no GET for the authorized-email list, only the PATCH this
 * drives (same gap the Share modal has) — so, like that modal, this always
 * starts with an empty People/Domain list even when access is already
 * Email from a previous save. Only the access card itself is
 * preselected, from `initialAccess`. Saving here only patches the
 * publishing settings so they show preselected in the Share modal
 * afterwards — it never publishes anything.
 */
export const AccessSharingSection: React.FC<AccessSharingSectionProps> = ({
  shareUrl,
  initialAccess,
  onAccessChange,
  onPasswordChange,
  onEmailsChange,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  // `null` = user hasn't clicked a card yet, so `access` is derived from
  // `initialAccess` below. Once they click, this holds their choice and
  // wins over any later `initialAccess` value — same "don't stomp a click
  // that happens before the fetch resolves" guarantee as `SlugSection`'s
  // title suggestion, without a hydration effect.
  const [overrideAccess, setOverrideAccess] = useState<ShareAccess | null>(
    null
  );
  // Real hydration is still pending (`initialAccess` undefined) and the
  // user hasn't clicked yet — render a skeleton instead of guessing "public".
  const isLoading = overrideAccess === null && initialAccess === undefined;
  const access = overrideAccess ?? initialAccess ?? "public";
  // Mode last saved on the server. Picking Password or Email saves nothing
  // until a valid password / first recipient, so this can differ from `access`.
  const [overrideSavedAccess, setOverrideSavedAccess] =
    useState<ShareAccess | null>(null);
  const savedAccess = overrideSavedAccess ?? initialAccess ?? "public";
  // Email picked over a saved Public/Password: not saved until a first recipient.
  const isPendingEmail = access === "email" && savedAccess !== "email";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // Last password actually patched — skips a re-send on a blur with no edit.
  const lastCommittedPasswordRef = useRef("");
  const [restriction, setRestriction] = useState<ShareRestriction>("people");
  const [emailDraft, setEmailDraft] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [domain, setDomain] = useState("");
  const [csvNote, setCsvNote] = useState("");
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [domainInvalid, setDomainInvalid] = useState(false);
  // Last domain actually patched — guards `handleDomainBlur` against
  // re-sending the same value (e.g. a blur with no edit in between).
  const lastCommittedDomainRef = useRef("");

  const handleAccessChange = (next: ShareAccess) => {
    if (next === access) return;
    setOverrideAccess(next);
    // Leaving Password: empty fields on return, so they never look saved when they aren't.
    if (access === "password") {
      setPassword("");
      setConfirmPassword("");
    }
    // Password is saved on blur once valid; going back to the saved mode
    // (e.g. Email → Password → Email) has nothing to save.
    if (next === "password" || next === savedAccess) return;
    // The first recipient replaces the server list on → Email; start empty locally.
    if (next === "email") {
      setEmails([]);
      setDomain("");
      setEmailDraft("");
      setCsvNote("");
      setEmailInvalid(false);
      setDomainInvalid(false);
      lastCommittedDomainRef.current = "";
      // → Email waits for a first recipient (MED-7325), see `saveEmails`.
      return;
    }
    setOverrideSavedAccess(next);
    lastCommittedPasswordRef.current = "";
    onAccessChange(next);
  };

  const changeEmailDraft = (value: string) => {
    setEmailDraft(value);
    setEmailInvalid(false);
  };

  const changeDomain = (value: string) => {
    setDomain(value);
    setDomainInvalid(false);
  };

  // While Email is pending, the first recipient saves access + list together.
  const saveEmails = (next: string[]) => {
    if (isPendingEmail) {
      setOverrideSavedAccess("email");
      lastCommittedPasswordRef.current = "";
      onAccessChange("email", next);
      return;
    }
    onEmailsChange(next);
  };

  // `saveEmails` triggers a PATCH — kept out of the `setEmails` updater
  // itself (React may invoke a state updater more than once, e.g. Strict
  // Mode) and called once, after the next value is computed.
  const commitEmails = (next: string[]) => {
    setEmails(next);
    // The PATCH replaces the whole list, so the domain is gone on the server.
    setDomain("");
    setDomainInvalid(false);
    lastCommittedDomainRef.current = "";
    saveEmails(next);
  };

  const addEmail = () => {
    const value = emailDraft.trim();
    if (!value) return;
    if (!isValidShareEmail(value)) {
      setEmailInvalid(true);
      return;
    }
    if (emails.includes(value)) return;
    commitEmails([...emails, value]);
    setEmailDraft("");
  };

  const removeEmail = (email: string) => {
    commitEmails(emails.filter((x) => x !== email));
  };

  const handleCsvFile = async (file: File) => {
    try {
      const parsed = await parseCsvEmails(file);
      if (parsed.length === 0) {
        setCsvNote(t("experienceV2.share.people.csvNoEmails"));
        return;
      }
      const next = Array.from(new Set([...emails, ...parsed]));
      const importedCount = next.length - emails.length;
      // Nothing new: the list on the server is already the same.
      if (importedCount > 0) {
        commitEmails(next);
      }
      setCsvNote(
        t("experienceV2.share.people.csvImported", { count: importedCount })
      );
    } catch {
      setCsvNote(t("experienceV2.share.people.csvNoEmails"));
    }
  };

  const handlePasswordBlur = () => {
    if (!isValidSharePassword(password, confirmPassword)) return;
    if (password === lastCommittedPasswordRef.current) return;
    lastCommittedPasswordRef.current = password;
    setOverrideSavedAccess("password");
    onPasswordChange?.(password);
  };

  const handleDomainBlur = () => {
    const trimmed = domain.trim();
    if (trimmed && !isValidShareDomain(trimmed)) {
      setDomainInvalid(true);
      return;
    }
    // Pending Email: an empty domain is not a recipient, send nothing.
    if (isPendingEmail && !trimmed) return;
    if (trimmed === lastCommittedDomainRef.current) return;
    lastCommittedDomainRef.current = trimmed;
    // The PATCH replaces the whole list, so the People emails are gone too.
    setEmails([]);
    saveEmails(trimmed ? [`*@${trimmed}`] : []);
  };

  return (
    <>
      <SectionHeading>{t(`${KEY}.heading`)}</SectionHeading>
      <SectionSubtitle $token={token}>{t(`${KEY}.subtitle`)}</SectionSubtitle>
      <Flex vertical gap={16}>
        {isLoading ? (
          <Skeleton variant="rectangular" height={160} />
        ) : (
          <ShareAccessFields
            access={access}
            onAccessChange={handleAccessChange}
            shareUrl={shareUrl}
            restriction={restriction}
            onRestrictionChange={setRestriction}
            emailDraft={emailDraft}
            onEmailDraftChange={changeEmailDraft}
            onAddEmail={addEmail}
            emails={emails}
            onRemoveEmail={removeEmail}
            domain={domain}
            onDomainChange={changeDomain}
            onDomainBlur={handleDomainBlur}
            onCsvFile={(file) => void handleCsvFile(file)}
            csvNote={csvNote}
            password={password}
            onPasswordChange={setPassword}
            confirmPassword={confirmPassword}
            onConfirmPasswordChange={setConfirmPassword}
            onPasswordBlur={handlePasswordBlur}
            emailError={
              emailInvalid
                ? t("experienceV2.share.validation.invalidEmail")
                : undefined
            }
            domainError={
              domainInvalid
                ? t("experienceV2.share.validation.invalidDomain")
                : undefined
            }
            validationError={
              // One message at a time: the open tab's invalid-value error replaces this hint.
              isPendingEmail &&
              !(restriction === "people" ? emailInvalid : domainInvalid)
                ? t("experienceV2.share.validation.recipientRequired")
                : undefined
            }
          />
        )}
      </Flex>
    </>
  );
};

export default AccessSharingSection;
